import { z } from "zod";

import type { DbEntity } from "../../../generated/prisma-models";
import { AnyExternalToolActionsSchema } from "../../../schemas/AgentExternalTool/actions/__json.schema";
import { ApifyAuthSchema } from "../../../schemas/AgentExternalTool/auth/Apify.schema";
import {
  CustomManifestAuthSchema,
  CustomManifestAuthTypeSchema
} from "../../../schemas/AgentExternalTool/auth/CustomManifest/__json.schema";
import { PipedreamAuthSchema } from "../../../schemas/AgentExternalTool/auth/Pipedream.schema";
import { ToolCustomHeadersSchema } from "../../../schemas/AgentExternalTool/customHeaders.schema";
import { ToolDefaultConfigSchema } from "../../../schemas/AgentExternalTool/defaultConfig.schema";
import { ToolExtractKeysSchema } from "../../../schemas/AgentExternalTool/extractKeys.schema";
import { ToolNodeConfigSchema } from "../../../schemas/AgentExternalTool/node.schema";
import { optionalSafeExternalUrlSchema } from "../../../shared/network/safe-external-url.schema";

// Clean union for API responses — no z.preprocess needed (repository already normalised the data).
// Using AnyExternalToolAuthSchema (which wraps z.preprocess) causes TypeScript to infer {} for the
// output type when used in complex schema compositions. This explicit union avoids that.
const ExternalToolAuthResponseSchema = z.union([
  PipedreamAuthSchema,
  ApifyAuthSchema,
  CustomManifestAuthSchema
]);

import { ApiSuccessSchema } from "../../common/response.schema";

// ---------------------------------------------------------------------------
// Response schema — ExternalTool detail
// ---------------------------------------------------------------------------

export const ExternalToolDetailSchema = z.object({
  // Identifiers
  id: z.string(),
  type: z.literal("EXTERNAL_TOOL"),
  externalToolType: z.custom<DbEntity.AgentExternalToolType>(),
  organizationId: z.string().nullable(),

  // Names
  name: z.string().nullable(),
  privateName: z.string(),
  publicName: z.string().nullable(),

  // Descriptions
  publicDescription: z.string(),
  privateDescription: z.string().nullable(),
  descriptionHL: z.string(),

  // URLs
  imageUrl: z.string(),
  endpointUrl: z.string().nullable(),
  documentationUrl: z.string().nullable(),
  // Free-text documentation body (distinct from `documentationUrl`, a link).
  // Set on external-tool create; surfaced so callers can round-trip it.
  documentation: z.string().nullable(),
  websiteUrl: z.string().nullable(),
  contactEmail: z.string().nullable(),
  manifestUrl: z.string().nullable(),
  callbackUrl: z.string().nullable(),
  successMessage: z.string().nullable(),

  // OpenAPI / query
  openAPISpec: z.string().nullable(),
  queryBody: z.string().nullable(),
  queryHeader: z.string().nullable(),
  queryMethod: z.string().nullable(),
  typeVersion: z.string().nullable(),
  responseKey: z.array(z.string()),
  callback: z.boolean().nullable(),

  // Json fields — discriminated by externalToolType column
  auth: ExternalToolAuthResponseSchema.nullable(),
  // Custom HTTP headers (e.g. User-Agent) applied to every action, independent
  // of auth — see NEX-2017. Null when none configured.
  customHeaders: ToolCustomHeadersSchema.nullable(),
  actions: AnyExternalToolActionsSchema.nullable(),
  // CREATOR-filtered actions — only non-CREATOR-handler fields visible to the user.
  // Populated by tools-marketplace.service.ts#getPublicActionsFromExternalTool (NEX-1432).
  userActions: z.array(z.object({ disabled: z.boolean().optional() }).passthrough()).optional(),
  node: ToolNodeConfigSchema, // CUSTOM_MANIFEST only — workflow builder UI config
  defaultConfig: ToolDefaultConfigSchema, // CUSTOM_MANIFEST only — pre-filled action defaults
  extractKeys: ToolExtractKeysSchema, // CUSTOM_MANIFEST only — API response key filter

  // Status
  status: z.custom<DbEntity.ToolPublicationStatus>(),
  privacyStatus: z.custom<DbEntity.ToolPrivacyStatus>(),

  // Counts
  numberOfCalls: z.number().nullable(),
  numberOfUsers: z.number().nullable(),
  tokenUsage: z.number().nullable(),
  canUseOwnKey: z.boolean(),

  // Ownership
  isOwnedByUser: z.boolean(),
  creatorName: z.string().optional(),

  // Auth UI helpers — the auth.type value surfaced for UI rendering
  authType: CustomManifestAuthTypeSchema.optional(),

  // Help content
  helper: z.string().nullable(),
  helperHL: z.string().nullable(),

  // Timestamps
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

export type ExternalToolDetail = z.infer<typeof ExternalToolDetailSchema>;

// ---------------------------------------------------------------------------
// Update body schema — fields that can be updated via PATCH
// ---------------------------------------------------------------------------

export const UpdateExternalToolBodySchema = z.object({
  // Every field stays optional — PATCH is a partial update and omitting a field
  // must keep it unchanged. Neither NAME can be BLANKED: `privateName` is the
  // identifier the model sees in the function-calling schema, so an empty
  // string leaves the agent a nameless function, and `publicName` is the label
  // the operator reads in the tool list. Omit either to leave it be.
  publicName: z.string().trim().min(1, "publicName must not be blank").optional(),
  privateName: z.string().trim().min(1, "privateName must not be blank").optional(),
  publicDescription: z.string().optional(),
  privateDescription: z.string().optional(),
  descriptionHL: z.string().optional(),
  // TENANT-WRITTEN. `null` clears it; `""` is the console's spelling of absent
  // (see `optionalSafeExternalUrlSchema`); anything else must be safe https.
  endpointUrl: optionalSafeExternalUrlSchema.nullable().optional(),
  openAPISpec: z.string().nullable().optional(),
  auth: ExternalToolAuthResponseSchema.optional(),
  // Pass `{}` to clear all custom headers, or omit to leave unchanged.
  customHeaders: ToolCustomHeadersSchema.optional(),
  actions: AnyExternalToolActionsSchema.optional(),
  node: ToolNodeConfigSchema.optional(),
  typeVersion: z.string().nullable().optional(),
  manifestUrl: z.string().nullable().optional(),
  queryBody: z.string().nullable().optional(),
  queryHeader: z.string().nullable().optional(),
  queryMethod: z.string().nullable().optional(),
  responseKey: z.array(z.string()).optional(),
  callback: z.boolean().nullable().optional(),
  callbackUrl: z.string().nullable().optional(),
  successMessage: z.string().nullable().optional(),
  status: z.custom<DbEntity.ToolPublicationStatus>().optional(),
  documentationUrl: z.string().nullable().optional(),
  contactEmail: z.string().nullable().optional(),
  websiteUrl: z.string().nullable().optional()
});

export type UpdateExternalToolBody = z.infer<typeof UpdateExternalToolBodySchema>;

// ---------------------------------------------------------------------------
// ZExternalTools — NexusApi contract
// ---------------------------------------------------------------------------

export const ZExternalTools = {
  Get: {
    method: "GET" as const,
    path: "/api/external-tools/:id",
    // UUID at the seam — the underlying column is @db.Uuid, so anything else
    // would surface as a Prisma validation 500. Fail cleanly with 400 here.
    PathVars: z.object({ id: z.string().uuid() }),
    Response: ApiSuccessSchema(ExternalToolDetailSchema)
  },

  Update: {
    method: "PATCH" as const,
    path: "/api/external-tools/:id",
    PathVars: z.object({ id: z.string().uuid() }),
    Body: UpdateExternalToolBodySchema,
    Response: ApiSuccessSchema(z.object({ updated: z.boolean() }))
  },

  Delete: {
    method: "DELETE" as const,
    path: "/api/external-tools/:id",
    PathVars: z.object({ id: z.string().uuid() }),
    // Strict literal "true" or omitted. Any other value (e.g. "false", "1")
    // is rejected with 400 — the controller parses against this schema and
    // throws BadRequestException on failure. `z.coerce.boolean()` would
    // wrongly treat "false" as truthy, hence the literal.
    Params: z.object({ force: z.literal("true").optional() }),
    Response: ApiSuccessSchema(z.object({ deleted: z.boolean() }))
  },

  DeleteCredential: {
    method: "DELETE" as const,
    path: "/api/external-tools/credentials/:id",
    PathVars: z.object({ id: z.string().uuid() }),
    Response: ApiSuccessSchema(z.object({ deleted: z.boolean() }))
  }
} as const;
